import React, { useState } from "react";
import { useTranslation } from "@/langs/useTranslation";
import dayjs from "dayjs";
import { numRegex } from "@/helpers/regex";
import {
  dateInLocal,
  timeInLocal,
  timeInNormal,
  // scheduledDateInUTC,
  scheduledDateInLocal
} from "@/helpers/datetimeHandlers";
import AddEventDialog from "@/components/powerManagement/AddEventDialog";
import { dotColor } from "@/helpers/powerManagerHelper";
import { deleteEventApi, getEvenApi } from "@/apis/powerManagerApi";
import { createEventApi, editEventApi } from "@/apis/apiCollection";
import textNoty from "@/helpers/textNoty";
import modalConfirm from "@/components/Modal/modalConfirm";
import { toast } from "@/apis/request";
import isEmpty from "lodash/isEmpty";

const ScheduleEventWrapper = ({
  editData,
  isDelete,
  onAddSuccess,
  onEditSuccess,
  onDeleteSuccess,
  children
}) => {
  const { t } = useTranslation("powerManagement");
  const [dialogLoading, setDialogLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [addDialigOpen, setAddDialogOpen] = useState(false);
  const [time, setTime] = useState(new Date());
  const [task, setTask] = useState("start");
  const [ext, setExt] = useState("");
  const [startDate, setStartDate] = useState(new Date());
  const [times, setTimes] = useState(1);
  const [loopType, setLoopType] = useState("daily");
  const [dailyType, setDailyType] = useState("every");
  const [dailyTimes, setDailyTimes] = useState(1);
  const [weeklyTimes, setWeeklyTimes] = useState(1);
  const [weeklySelect, setWeeklySelect] = useState({
    mon: false,
    tue: false,
    wed: false,
    thu: false,
    fri: false,
    sat: false,
    sun: false
  });
  const [endType, setEndType] = useState("datetime");
  const [endDate, setEndDate] = useState(new Date());

  // delete event
  const deleteHandler = info => {
    modalConfirm({
      title: t({ defaultMessage: "提示" }),
      content: t({ defaultMessage: "确定要删除吗？" }),
      onConfirm: async () => {
        try {
          const res = await deleteEventApi(info.id, t, t("noty.delete"));
          if (res && res.status && res.status === "success") {
            setAddDialogOpen(false);
            onDeleteSuccess?.();
          }
        } finally {
          setDialogLoading(false);
        }
      }
    });
  };

  // select date to show add dialog
  const handleCreate = selectInfo => {
    if (loading) {
      return;
    }
    const currentDay = dayjs().format("YYYY-MM-DD");
    const start = dayjs(selectInfo.startStr).format("YYYY-MM-DD");
    if (dayjs(start).isBefore(currentDay)) {
      textNoty(t("dialog.minDateMessage"));
      return;
    }
    setLoopType("daily");
    setExt("");
    setTimes(1);
    setEndType("datetime");
    setDailyType("every");
    setDailyTimes(1);
    setWeeklySelect({
      mon: false,
      tue: false,
      wed: false,
      thu: false,
      fri: false,
      sat: false,
      sun: false
    });
    setWeeklyTimes(1);
    setTime(new Date());
    setStartDate(selectInfo.startStr);
    setEndDate(dayjs(selectInfo.endStr).subtract(1, "days"));
    setAddDialogOpen(true);
    setTask("start");
  };

  //edit events
  const handleEdit = async info => {
    let res = await getEvenApi(info.id);
    let data = res.events;
    if (data) {
      setStartDate(dateInLocal(data.json.start_date || new Date()));
      setEndDate(dateInLocal(data.json.end_date || new Date()));
      setTask(info.task_type);
      setExt(
        info.task_type === "start_app" || info.task_type === "close_app"
          ? info.ext
          : { id: info.ext, name: info.ext }
      );
      setTime(data.json.end_date || new Date());
      setLoopType(data.schedule_type || "daily");
      setEndType(data.json.end_type || "datetime");
      setTimes(data.json.run_times || 1);
      if (data.schedule_type === "daily") {
        setDailyTimes(Number(data.json.cycle_value || 1));
        setWeeklyTimes(1);
        setWeeklySelect({
          mon: false,
          tue: false,
          wed: false,
          thu: false,
          fri: false,
          sat: false,
          sun: false
        });
        setDailyType(data.json.cycle_type || "every");
      } else {
        if (data.json.cycle_days) {
          let result = {
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
            sun: false
          };
          data.json.cycle_days.forEach(e => (result[e] = true));
          setWeeklySelect(result);
        } else {
          setWeeklySelect({
            mon: false,
            tue: false,
            wed: false,
            thu: false,
            fri: false,
            sat: false,
            sun: false
          });
        }
        setDailyType("every");
        setDailyTimes(1);
        setWeeklyTimes(data.json.cycle_value || 1);
      }
      setAddDialogOpen(true);
    }
  };

  // add events
  const onConfirmAdd = async () => {
    const currentDay = dayjs().format("YYYY-MM-DD");
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    const startDayAddOne = dayjs(start).add(1, "days");

    // if (task === "start" && isEmpty(ext)) {
    //   textNoty(t({ defaultMessage: "设备SN不能为空" }));
    //   return;
    // }

    if (task === "start_app" && isEmpty(ext)) {
      textNoty(t({ defaultMessage: "应用路径不能为空" }));
      return;
    }

    // loop type daily and every days no value
    if (
      loopType === "daily" &&
      dailyType === "every" &&
      (!dailyTimes || dailyTimes < 1 || !numRegex.test(dailyTimes))
    ) {
      textNoty(t("dialog.loopTypeTimeAtLeastOneDay"));
      return;
    }

    if (loopType === "weekly") {
      // loop type weekly and every weeks no value
      if (!weeklyTimes || weeklyTimes < 1 || !numRegex.test(weeklyTimes)) {
        textNoty(t("dialog.loopTypeTimeAtLeastOneWeek"));
        return;
      }
      if (weeklyTimes > 4) {
        return;
      }
      // loop type weekly and no select day
      if (!Object.values(weeklySelect).includes(true)) {
        textNoty(t("dialog.loopTypeTimeAtLeastSelectADay"));
        return;
      }
      // End date equal start
      if (endType === "datetime" && dayjs(end).isBefore(startDayAddOne)) {
        textNoty(t("dialog.oneDayLater"));
        return;
      }
    }
    // end date less than start date
    if (endType === "datetime" && dayjs(end).isBefore(start)) {
      textNoty(t("dialog.endLessThanStart"));
      return;
    }
    // no times
    if (endType === "times") {
      if (!times || times < 1 || !numRegex.test(times)) {
        textNoty(t("dialog.noTimes"));
        return;
      }
      if (times > 30) {
        return;
      }
    }
    // start date less than current date
    if (dayjs(start).isBefore(currentDay)) {
      textNoty(t("dialog.minDateMessage"));
      return;
    }
    try {
      setDialogLoading(true);
      const res = await createEventApi(
        task,
        ext,
        loopType,
        dailyType,
        dailyTimes,
        weeklyTimes,
        weeklySelect,
        endType,
        times,
        `${scheduledDateInLocal(dayjs(start + " " + timeInNormal(time)))}`,
        `${scheduledDateInLocal(dayjs(end + " " + timeInNormal(time)))}`
      );
      setAddDialogOpen(false);
      onAddSuccess?.(res.event);
      toast.success(t("noty.create"));
    } finally {
      setDialogLoading(false);
    }
  };

  const onConfirmEdit = async () => {
    const start = dayjs(startDate).format("YYYY-MM-DD");
    const end = dayjs(endDate).format("YYYY-MM-DD");
    const startDayAddOne = dayjs(start).add(1, "days");

    // if (task === "start" && !ext) {
    //   textNoty(t({ defaultMessage: "设备SN不能为空" }));
    //   return;
    // }

    if (task === "start_app" && !ext) {
      textNoty(t({ defaultMessage: "应用路径不能为空" }));
      return;
    }

    // loop type daily and every days no value
    if (
      loopType === "daily" &&
      dailyType === "every" &&
      (!dailyTimes || dailyTimes < 1 || !numRegex.test(dailyTimes))
    ) {
      textNoty(t("dialog.loopTypeTimeAtLeastOneDay"));
      return;
    }

    if (loopType === "weekly") {
      // loop type weekly and every weeks no value
      if (!weeklyTimes || weeklyTimes < 1 || !numRegex.test(weeklyTimes)) {
        textNoty(t("dialog.loopTypeTimeAtLeastOneWeek"));
        return;
      }
      if (weeklyTimes > 4) {
        return;
      }
      // loop type weekly and no select day
      if (!Object.values(weeklySelect).includes(true)) {
        textNoty(t("dialog.loopTypeTimeAtLeastSelectADay"));
        return;
      }
      // End date equal start
      if (endType === "datetime" && dayjs(end).isBefore(startDayAddOne)) {
        textNoty(t("dialog.oneDayLater"));
        return;
      }
    }
    // end date less than start date
    if (endType === "datetime" && dayjs(end).isBefore(start)) {
      textNoty(t("dialog.endLessThanStart"));
      return;
    }
    // no times
    if (endType === "times") {
      if (!times || times < 1 || !numRegex.test(times)) {
        textNoty(t("dialog.noTimes"));
        return;
      }
      if (times > 30) {
        return;
      }
    }

    try {
      setDialogLoading(true);
      const res = await editEventApi(
        editData.id,
        task,
        ext,
        loopType,
        dailyType,
        dailyTimes,
        weeklyTimes,
        weeklySelect,
        endType,
        times,
        `${scheduledDateInLocal(dayjs(start + " " + timeInNormal(time)))}`,
        `${scheduledDateInLocal(dayjs(end + " " + timeInNormal(time)))}`
      );
      toast.success(t("noty.update"));
      setAddDialogOpen(false);
      onEditSuccess?.(res.event);
    } finally {
      setDialogLoading(false);
    }
  };

  const closeAddOrEdit = () => {
    setAddDialogOpen(false);
  };

  return (
    <>
      {React.cloneElement(React.Children.only(children), {
        onClick: e => {
          if (!!children.props.onClick) {
            children.props.onClick(e);
          }
          if (!!editData) {
            if (isDelete) {
              deleteHandler(editData);
            } else {
              handleEdit(editData);
            }
          } else {
            handleCreate({
              startStr: dayjs().format("YYYY-MM-DD"),
              endStr: dayjs().add(1, "d").format("YYYY-MM-DD")
            });
          }
        }
      })}
      <AddEventDialog
        loading={dialogLoading}
        setTime={setTime}
        time={time}
        task={task}
        setTask={setTask}
        ext={ext}
        setExt={setExt}
        onConfirmEdit={onConfirmEdit}
        onConfirmAdd={onConfirmAdd}
        setStartDate={setStartDate}
        startDate={startDate}
        setEndDate={setEndDate}
        endDate={endDate}
        open={addDialigOpen}
        times={times}
        setTimes={setTimes}
        setEndType={setEndType}
        endType={endType}
        dotColor={dotColor}
        timeInLocal={timeInLocal}
        setLoopType={setLoopType}
        loopType={loopType}
        dailyType={dailyType}
        setDailyType={setDailyType}
        dailyTimes={dailyTimes}
        editData={editData}
        setDailyTimes={setDailyTimes}
        weeklyTimes={weeklyTimes}
        setWeeklyTimes={setWeeklyTimes}
        weeklySelect={weeklySelect}
        setWeeklySelect={setWeeklySelect}
        onClose={() => closeAddOrEdit(false)}
        deleteHandler={deleteHandler}
      />
    </>
  );
};

export default ScheduleEventWrapper;
